import { useRef, useLayoutEffect } from 'react';
import { getOwnerWindow, makeSimpleDomRect } from '../utils/dom.js';
import { addListener } from '../utils/event.js';
import { callEach } from '../utils/fn.js';
import { observePosition } from '../utils/observe-position.js';
import { observeResize } from '../utils/observe-resize.js';

const MAX_POSITIONING_ATTEMPTS = 5;
const ANCESTOR_POSITIONING_MICROTASK_ATTEMPTS = 5;
/** A global cache of all positioned popups, used for collision detection. */
const PopupPositionCache = new WeakMap();
function usePopup({ open = true, anchorRef, popupRef, scrollRef, contentRef, positioner, onPosition, sticky = false, }) {
    const stickyAreaRef = useRef(undefined);
    useLayoutEffect(() => {
        if (!open || !anchorRef.current)
            return;
        const ownerWindow = getOwnerWindow(popupRef.current ?? anchorRef.current);
        const abortController = new AbortController();
        stickyAreaRef.current = undefined;
        function reposition() {
            const anchor = anchorRef.current;
            const popup = popupRef.current;
            if (!anchor || !popup)
                return;
            const scroll = scrollRef?.current;
            void doPositioning(anchor, popup, scroll, positioner, onPosition, sticky ? stickyAreaRef.current : undefined, abortController.signal).then((result) => {
                if (abortController.signal.aborted || !result)
                    return;
                if (sticky)
                    stickyAreaRef.current = result.context.positionArea ?? undefined;
            });
        }
        reposition();
        return callEach(() => abortController.abort(), 
        // Re-anchor (and re-flip if forced) when the content's own size changes, e.g. options finish
        // loading. Watching the content element (not the popup box) means grow/scroll/clip box
        // adjustments — which don't change the content — don't trigger repositioning.
        observeResize(contentRef ?? popupRef, (_, __, first) => {
            // ResizeObserver fires once on observe; we've already positioned, so ignore that one.
            if (first)
                return;
            reposition();
        }), observePosition({
            anchor: anchorRef.current,
            onPositionChange: reposition,
        }), 
        // observePosition reports anchor movement (including a trigger pinned to a resizing viewport
        // edge), but a resize can also invalidate the placement without moving the anchor — e.g. a
        // shorter viewport no longer fits the popup below the trigger. Reposition for that case here.
        addListener(ownerWindow, 'resize', reposition));
        // We only want to set up positioning once when opened.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
}
async function doPositioning(anchor, popup, scroll, positioner, onPosition, positionOverride, signal) {
    const ownerWindow = getOwnerWindow(popup);
    // Portaled popups are siblings, but a submenu's anchor remains a DOM descendant of its parent
    // popup, so `closest()` finds the popup whose position this submenu depends on.
    const ancestorPopup = anchor.closest('[data-fpl-popup]');
    if (ancestorPopup &&
        !ancestorPopup.hasAttribute('data-fpl-positioned') &&
        !(await waitForAncestorPopupPosition(anchor, ancestorPopup, signal))) {
        return;
    }
    if (signal?.aborted || !anchor.isConnected || !popup.isConnected)
        return;
    let anchorRect;
    let popupRect;
    let scale = 1;
    let attempt = 0;
    // A previous pass may have clipped the popup, leaving an explicit height. That caps the scroll
    // container's clientHeight, and `scrollHeight` can't report less than clientHeight — so we'd
    // measure the clipped height instead of the natural content height. Clear it first so we measure
    // the true size; `apply()` below re-sets a height only if this pass clips again.
    popup.style.removeProperty('height');
    while (attempt < MAX_POSITIONING_ATTEMPTS) {
        if (signal?.aborted || !anchor.isConnected || !popup.isConnected)
            return;
        anchorRect = anchor.getBoundingClientRect();
        ({ scale, rect: popupRect } = getTrueRect(scroll ?? popup));
        if (isInvalidDomRect(anchorRect) || isInvalidDomRect(popupRect)) {
            await waitAnimationFrame(ownerWindow);
            ++attempt;
        }
        else {
            break;
        }
    }
    if (signal?.aborted || !anchor.isConnected || !popup.isConnected)
        return;
    const result = positioner.run(anchorRect, popupRect, {
        targetScale: scale,
        positionOverride,
        ownerWindow,
    });
    result.target.apply(popup, scroll);
    popup.setAttribute('data-fpl-positioned', '');
    onPosition?.(result.target);
    PopupPositionCache.set(popup, result.target);
    return result;
}
/**
 * Wait until a nested popup's nearest popup ancestor has finished its initial positioning.
 * Same-commit positioning usually completes within a few microtasks; after that, continue polling
 * once per frame without a timeout until positioning finishes or either element disconnects.
 */
async function waitForAncestorPopupPosition(anchor, ancestorPopup, signal) {
    let microtaskAttempts = 0;
    const ownerWindow = getOwnerWindow(ancestorPopup);
    while (!ancestorPopup.hasAttribute('data-fpl-positioned')) {
        if (signal?.aborted || !anchor.isConnected || !ancestorPopup.isConnected)
            return false;
        if (microtaskAttempts < ANCESTOR_POSITIONING_MICROTASK_ATTEMPTS) {
            await Promise.resolve();
            ++microtaskAttempts;
            continue;
        }
        await waitAnimationFrame(ownerWindow);
    }
    return true;
}
function waitAnimationFrame(ownerWindow) {
    return new Promise((resolve) => ownerWindow.requestAnimationFrame(resolve));
}
function getTrueRect(el) {
    const rect = el.getBoundingClientRect();
    const scale = rect.width / el.clientWidth;
    return {
        scale,
        rect: makeSimpleDomRect(rect.left, rect.top, el.scrollWidth * scale, el.scrollHeight * scale),
    };
}
function isInvalidDomRect(rect) {
    return rect.width === 0 && rect.height === 0;
}

export { PopupPositionCache, usePopup };
//# sourceMappingURL=use-popup.js.map
